/**
 * @file CompanyPicker.tsx
 * @brief Company selection control — pill trigger that opens a modal
 *        sheet of pill-shaped company rows.
 *
 * @details
 * Mobile parity layer: matches the `VZCountryPicker` / `VZLanguagePicker`
 * UX (full-height modal, pill items, brand-coloured selection, search
 * at top). Used inside the Users dialogs to attach a user to a company.
 *
 * - List items come from `useCompanyOptions(search)` (TanStack Query,
 *   debounced via `useDebouncedValue`). The picker forwards the search
 *   string to the hook (controlled mode of `<PickerSheet>`).
 * - When the parent only holds the company id, `useCompany(id)`
 *   resolves the name so the trigger pill always shows a label.
 *
 * @dependencies
 * - features/companies/api (useCompany, useCompanyOptions)
 * - shared/hooks/useDebouncedValue
 * - components/ui/PickerSheet
 *
 * @author DES
 * @date 26-05-2026
 * @updated 01-10-2026: `variant="pill"`: the page-header `HeaderFilterMenu`
 *          (a searchable dropdown, not the sheet)
 */

import { useMemo, useState } from "react";
import { Building2, ChevronsUpDown } from "lucide-react";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { cn } from "@/shared/theme/cn";
import { useCompany, useCompanyOptions } from "../api";

interface CompanyPickerProps {
  value?: string | null;
  onChange: (companyId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  /** Extra classes for the trigger button (e.g. flush filter cells). */
  triggerClassName?: string;
  /** `pill`: the 56px page-header filter beside the date circle. */
  variant?: "field" | "pill";
}

const ANY_COMPANY = "__any__";

function CompanyIcon({ size }: { size: "sm" | "md" }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand",
        size === "md" ? "h-9 w-9" : "h-7 w-7",
      )}
    >
      <Building2 className={size === "md" ? "h-4 w-4" : "h-3.5 w-3.5"} aria-hidden />
    </span>
  );
}

interface CompanyPickerItem extends PickerSheetItem {
  domain?: string | null;
}

export function CompanyPicker({
  value,
  onChange,
  placeholder = "Select a company…",
  disabled,
  size = "md",
  triggerClassName,
  variant = "field",
}: CompanyPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);

  // ----------------------------------------------------------------------
  // Resolve the currently selected company so the trigger can show the
  // name even when the parent only stored the id.
  // ----------------------------------------------------------------------
  const selected = useCompany(value ?? null, Boolean(value));

  // ----------------------------------------------------------------------
  // Fetch options for the open modal — debounced, paged from backend.
  // ----------------------------------------------------------------------
  const options = useCompanyOptions(debouncedSearch, open);
  const rows = useMemo(() => options.data?.companies ?? [], [options.data?.companies]);

  const items: CompanyPickerItem[] = useMemo(
    () =>
      rows.map((c) => ({
        id: c.id,
        label: c.name,
        subtitle: c.domain ?? undefined,
        domain: c.domain,
      })),
    [rows],
  );

  const triggerLabel = useMemo(() => {
    if (!value) return null;
    if (selected.data) return selected.data.name;
    const fromList = rows.find((r) => r.id === value);
    return fromList?.name ?? null;
  }, [value, selected.data, rows]);

  const handleOpen = (next: boolean) => {
    if (next) setSearch("");
    setOpen(next);
  };

  if (variant === "pill") {
    // The "any" row leads the list until a search narrows it.
    const pillItems: SearchableMenuItem[] = [
      ...(search.trim()
        ? []
        : [{ value: ANY_COMPANY, label: placeholder, icon: <CompanyIcon size="sm" /> }]),
      ...rows.map((c) => ({
        value: c.id,
        label: c.name,
        description: c.domain ?? undefined,
        icon: <CompanyIcon size="sm" />,
      })),
    ];
    return (
      <HeaderFilterMenu
        label={triggerLabel ?? (value && selected.isLoading ? "Loading…" : placeholder)}
        active={Boolean(value)}
        onClear={() => onChange(null)}
        icon={<CompanyIcon size="md" />}
        title="Filter by company"
        searchPlaceholder="Search companies"
        items={pillItems}
        loading={options.isFetching}
        disabled={disabled}
        query={search}
        onQueryChange={setSearch}
        onOpenChange={handleOpen}
        noMatchText={`No companies match “${debouncedSearch}”.`}
        emptyText="No companies yet. Create one from the Companies page."
        selected={value ?? ANY_COMPANY}
        onSelect={(id) => onChange(id === ANY_COMPANY ? null : id)}
      />
    );
  }

  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  return (
    <>
      <PickerSheet<CompanyPickerItem>
        anchor={
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex w-full items-center justify-between rounded-full border border-input bg-field px-5 text-sm transition-colors",
              triggerHeight,
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              !triggerLabel && "text-muted-foreground",
              triggerClassName,
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <Building2 className="h-4 w-4 shrink-0 opacity-60" />
              <span className="truncate">
                {triggerLabel ?? (value && selected.isLoading ? "Loading…" : placeholder)}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </button>
        }
        open={open}
        onOpenChange={handleOpen}
        title="Select company"
        subtitle="Attach this user to a tenant company."
        searchPlaceholder="Search companies…"
        items={items}
        selectedId={value ?? null}
        onSelect={(item) => onChange(item.id)}
        onClear={() => onChange(null)}
        loading={options.isFetching}
        searchValue={search}
        onSearchChange={setSearch}
        renderLeftAccessory={() => (
          <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
            <Building2 className="h-6 w-6" />
          </span>
        )}
        emptyText={
          debouncedSearch
            ? `No companies match “${debouncedSearch}”.`
            : "No companies yet — create one from the Companies page."
        }
      />
    </>
  );
}
